import type { SocketEvent } from '@sea-snag/shared';
import { SocketEvents } from '@sea-snag/shared';
import { io, type Socket } from 'socket.io-client';

import { getAccessToken } from './api-client';

const SOCKET_URL = (import.meta.env['VITE_API_URL'] as string) || 'http://localhost:3000';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface SocketError {
  message: string;
  code?: string;
}

// Type-safe event payloads
export interface SyncCompletedPayload {
  organizationId: string;
  integrationId: string;
  provider: string;
  result: {
    boatsCreated: number;
    boatsUpdated: number;
    boatsDeactivated: number;
  };
  timestamp: string;
}

export interface SyncFailedPayload {
  organizationId: string;
  integrationId: string;
  provider: string;
  error: string;
  timestamp: string;
}

export interface CompSetSyncDonePayload {
  compSetPid: string;
  compSetName: string;
  marketBoatCount: number;
  added?: number;
  removed?: number;
  timestamp: string;
}

export interface CompSetSyncFailedPayload {
  compSetId: string;
  error: string;
  timestamp: string;
}

export interface FirmSyncPhaseStartedPayload {
  phase: 'yachts' | 'availability' | 'reservations';
  total: number;
}

export interface FirmSyncPhaseProgressPayload {
  phase: 'yachts' | 'availability' | 'reservations';
  completed: number;
  total: number;
}

export interface FirmSyncPhaseCompletedPayload {
  phase: 'yachts' | 'availability' | 'reservations';
  stats: Record<string, number>;
}

export interface FirmSyncCompletedPayload {
  totalYachts: number;
  totalAvailability: number;
}

export interface FirmSyncFailedPayload {
  phase: 'yachts' | 'availability' | 'reservations';
  error: string;
  retryable: boolean;
}

export interface ServerToClientEvents {
  [SocketEvents.AUTH_SUCCESS]: () => void;
  [SocketEvents.AUTH_ERROR]: (error: SocketError) => void;
  [SocketEvents.USER_UPDATED]: (data: { userId: string }) => void;
  [SocketEvents.USER_DELETED]: (data: { userId: string }) => void;
  [SocketEvents.USER_STATUS_CHANGED]: (data: { userId: string; isOnline: boolean }) => void;
  [SocketEvents.NOTIFICATION_NEW]: (data: {
    id: string;
    type: string;
    title: string;
    message: string;
  }) => void;
  [SocketEvents.NOTIFICATION_READ]: (data: { notificationId: string }) => void;
  [SocketEvents.SYNC_COMPLETED]: (data: SyncCompletedPayload) => void;
  [SocketEvents.SYNC_FAILED]: (data: SyncFailedPayload) => void;
  [SocketEvents.COMP_SET_SYNC_DONE]: (data: CompSetSyncDonePayload) => void;
  [SocketEvents.COMP_SET_SYNC_FAILED]: (data: CompSetSyncFailedPayload) => void;
  [SocketEvents.FIRM_SYNC_PHASE_STARTED]: (data: FirmSyncPhaseStartedPayload) => void;
  [SocketEvents.FIRM_SYNC_PHASE_PROGRESS]: (data: FirmSyncPhaseProgressPayload) => void;
  [SocketEvents.FIRM_SYNC_PHASE_COMPLETED]: (data: FirmSyncPhaseCompletedPayload) => void;
  [SocketEvents.FIRM_SYNC_COMPLETED]: (data: FirmSyncCompletedPayload) => void;
  [SocketEvents.FIRM_SYNC_FAILED]: (data: FirmSyncFailedPayload) => void;
}

export interface ClientToServerEvents {
  ping: (callback: (response: { timestamp: number }) => void) => void;
  'join:org': (payload: { orgPid: string }) => void;
  'leave:org': (payload: { orgPid: string }) => void;
}

export type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: TypedSocket | null = null;

/**
 * Create and configure the Socket.io client
 */
export function createSocket(): TypedSocket {
  if (socket) {
    return socket;
  }

  const accessToken = getAccessToken();

  socket = io(SOCKET_URL, {
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 10000,
    auth: {
      token: accessToken,
    },
    transports: ['websocket', 'polling'],
  }) as TypedSocket;

  return socket;
}

/**
 * Get the existing socket instance or create a new one
 */
export function getSocket(): TypedSocket | null {
  return socket;
}

/**
 * Connect the socket with optional new token
 */
export function connectSocket(token?: string): TypedSocket {
  if (!socket) {
    socket = createSocket();
  }

  if (token) {
    socket.auth = { token };
  }

  if (!socket.connected) {
    socket.connect();
  }

  return socket;
}

/**
 * Disconnect and cleanup the socket
 */
export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/**
 * Update the socket auth token (useful after token refresh)
 */
export function updateSocketAuth(token: string): void {
  if (socket) {
    socket.auth = { token };
    // Reconnect with new token if currently connected
    if (socket.connected) {
      socket.disconnect();
      socket.connect();
    }
  }
}

// Re-export socket events for convenience
export { type SocketEvent, SocketEvents };
